﻿import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import type { OrganizerSessionResponse } from "@ticketnjoy/types";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ApiError, apiClient } from "@/lib/api-client";
import { useT } from "@/lib/i18n";
import { meQueryKey } from "@/features/organizers/api";
import { isOnboarded } from "@/pages/onboarding";

// Token tek kullanımlık — backend ikinci çağrıda 400 invalid_token döner.
// StrictMode/HMR ile component remount olduğunda yeni instance'ın effect'i de
// çalışır; aynı token için tek bir backend isteği paylaşmak adına module-level
// Promise cache. Her remount aynı promise'in then/catch'ini dinler, böylece
// component-bound mutation state'inin "stale" kalması sorunu yaşanmaz.
const verificationCache = new Map<string, Promise<OrganizerSessionResponse>>();

function verifyToken(token: string): Promise<OrganizerSessionResponse> {
  let promise = verificationCache.get(token);
  if (!promise) {
    promise = apiClient<OrganizerSessionResponse>("/organizers/verify-email", {
      method: "POST",
      body: { token },
    });
    verificationCache.set(token, promise);
  }
  return promise;
}

type Status = "verifying" | "success" | "error";

export function VerifyEmailPage() {
  const t = useT("auth.verifyPage");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState<Status>(token ? "verifying" : "error");
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [traceId, setTraceId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    verifyToken(token)
      .then((data) => {
        if (cancelled) return;
        queryClient.setQueryData(meQueryKey, data);
        setStatus("success");
        // Email doğrulandı → ilk kez geliyorsa onboarding wizard'a, yoksa
        // doğrudan dashboard'a. Flag localStorage'da; sıfırlamak isteyen
        // kullanıcı /onboarding URL'ini elden de açabilir.
        const target = isOnboarded() ? "/" : "/onboarding";
        setTimeout(() => {
          if (!cancelled) navigate(target, { replace: true });
        }, 1500);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setStatus("error");
        if (err instanceof ApiError) {
          setErrorDetail(err.detail);
          if (err.status >= 500) setTraceId(err.traceId);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, queryClient, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/40">
      <Card className="w-full max-w-md text-center">
        <CardHeader className="space-y-4">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full">
            {status === "verifying" && <Loader2 className="h-10 w-10 animate-spin text-primary" />}
            {status === "success" && <CheckCircle2 className="h-14 w-14 text-green-600" />}
            {status === "error" && <XCircle className="h-14 w-14 text-destructive" />}
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl">
              {status === "verifying" && t("verifyingTitle")}
              {status === "success" && t("successTitle")}
              {status === "error" && t("errorTitle")}
            </CardTitle>
            <CardDescription>
              {status === "verifying" && t("verifyingDescription")}
              {status === "success" && t("successDescription")}
              {status === "error" && (errorDetail ?? (token ? t("failed") : t("missing")))}
            </CardDescription>
            {traceId ? (
              <p className="text-xs text-muted-foreground">
                {t("supportCode")} <span className="font-mono">{traceId}</span>
              </p>
            ) : null}
          </div>
        </CardHeader>
        {status === "error" && (
          <CardContent className="space-y-3">
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">{t("login")}</Link>
            </Button>
            <Button asChild variant="ghost" className="w-full">
              <Link to="/signup">{t("signup")}</Link>
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
